// Client-side mirror of the server's PhoneNumbers normalization (app-service PublicCall module)
// for immediate validation feedback and display. The server re-normalizes and stays
// authoritative; the canonical E.164 value is what the server stores and compares.
//
// Pasted numbers often carry invisible presentation characters (WhatsApp wraps numbers in bidi
// embedding marks and uses non-breaking hyphens; RTL text adds LRM/RLM; contact cards use NBSP /
// narrow NBSP). Exactly these explicit sets are tolerated — anything else, including letters,
// extensions, '*', '#', a second '+', other format/override characters and control characters,
// is still rejected. The character policy, the 32-character input bound and all validation rules
// are identical to PhoneNumbers.cs; both suites run the shared table in testdata/phone-normalization.json.

const ISRAEL = "972";
const MAX_INPUT = 32;

/** Skipped anywhere: Unicode spaces (no ogham mark) and bidi marks, embeddings and isolates (no overrides). */
const IGNORED = new Set([
  "\u0020", "\u00A0", "\u2000", "\u2001", "\u2002", "\u2003", "\u2004", "\u2005", "\u2006", "\u2007",
  "\u2008", "\u2009", "\u200A", "\u202F", "\u205F", "\u3000",
  "\u200E", "\u200F", "\u061C", "\u202A", "\u202B", "\u202C", "\u2066", "\u2067", "\u2068", "\u2069"
]);
/** Copy artifacts tolerated only at the very start or end (a copied line, a BOM). */
const EDGE_ONLY = new Set(["\t", "\n", "\r", "\uFEFF"]);
/** Visual separators between digits: ASCII plus the Unicode hyphens/dashes used by phone UIs. */
const SEPARATORS = new Set(["-", ".", "(", ")", "\u2010", "\u2011", "\u2012", "\u2013"]);

const isEdge = (ch: string) => EDGE_ONLY.has(ch) || IGNORED.has(ch);

export function normalizePhone(input: string | null | undefined): string | null {
  if (!input || input.length > MAX_INPUT) return null;
  let start = 0;
  let end = input.length;
  while (start < end && isEdge(input[start]!)) start++;
  while (end > start && isEdge(input[end - 1]!)) end--;

  let plus = false;
  let started = false; // a digit or separator was seen: '+' is only valid before both
  let digits = "";
  for (let i = start; i < end; i++) {
    const ch = input[i]!;
    if (ch >= "0" && ch <= "9") { digits += ch; started = true; }
    else if (ch === "+") { if (plus || started) return null; plus = true; }
    else if (SEPARATORS.has(ch)) started = true;
    else if (!IGNORED.has(ch)) return null;
  }
  if (!digits) return null;

  let intl: string;
  if (plus) intl = digits;
  else if (digits.startsWith("00")) intl = digits.slice(2);
  else if (digits.startsWith("0")) intl = ISRAEL + digits.slice(1);
  else if (digits.startsWith(ISRAEL) && (digits.length === 11 || digits.length === 12)) intl = digits;
  else return null;

  if (!intl || intl.startsWith("0")) return null;

  if (intl.startsWith(ISRAEL)) {
    let national = intl.slice(ISRAEL.length);
    if (national.startsWith("0")) national = national.slice(1);
    if (!isValidIsraeliNational(national)) return null;
    intl = ISRAEL + national;
  }

  if (intl.length < 8 || intl.length > 15) return null;
  return "+" + intl;
}

/** Israeli numbers in local notation (050-123-4567); others as E.164. */
export function formatPhoneDisplay(e164: string): string {
  if (!e164.startsWith("+" + ISRAEL)) return e164;
  const national = "0" + e164.slice(1 + ISRAEL.length);
  if (national.length === 10) return `${national.slice(0, 3)}-${national.slice(3, 6)}-${national.slice(6)}`;
  if (national.length === 9) return `${national.slice(0, 2)}-${national.slice(2, 5)}-${national.slice(5)}`;
  return national;
}

function isValidIsraeliNational(n: string): boolean {
  if (n.length !== 8 && n.length !== 9) return false;
  if (n.startsWith("0")) return false;
  return n.length === 8 || n.startsWith("5") || n.startsWith("7");
}
